import type {
  CreateInviterResponse,
  GetInviterResponse,
  SubmitRegistrationRequest,
  SubmitRegistrationResponse,
  ListRegistrationsResponse,
  ListInvitersResponse,
  ErrorResponse,
  ValidationErrorResponse,
  InviterType,
} from "@invitation-system/shared";

// ─── Configuration ──────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
const DEFAULT_TIMEOUT_MS = 10_000;

// ─── Error Types ────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ErrorResponse | ValidationErrorResponse,
  ) {
    super(`API error ${status}: ${body.error}`);
    this.name = "ApiError";
  }
}

// ─── Auth Token ─────────────────────────────────────────────────────────────

/** Reads the current id_token from localStorage. Avoids closure timing issues. */
function getAuthToken(): string | null {
  try {
    return localStorage.getItem("id_token");
  } catch {
    return null;
  }
}

// ─── Internal Helpers ───────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> ?? {}),
  };

  // Add auth token for admin routes
  if (path.startsWith("/admin")) {
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      signal: controller.signal,
      headers,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({
        error: "unknown_error",
      }))) as ErrorResponse | ValidationErrorResponse;
      throw new ApiError(response.status, body);
    }

    return (await response.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

// ─── Public API Methods ─────────────────────────────────────────────────────

/** Options for type-specific inviter fields */
export interface CreateInviterOptions {
  type?: InviterType;
  // Sponsor fields
  orgName?: string;
  logoUrl?: string;
  // Speaker fields
  speakerName?: string;
  title?: string;
  talkTitle?: string;
  photoUrl?: string;
}

/**
 * Create a new inviter and get back the invitation URL.
 * POST /admin/inviters (requires auth)
 */
export async function createInviter(
  displayName: string,
  options?: CreateInviterOptions,
): Promise<CreateInviterResponse> {
  return request<CreateInviterResponse>("/admin/inviters", {
    method: "POST",
    body: JSON.stringify({ displayName, ...options }),
  });
}

/**
 * Look up an inviter by their identifier.
 * GET /inviters/{inviterId} (public)
 * Returns null if the inviter does not exist (404).
 */
export async function getInviter(
  inviterId: string,
): Promise<GetInviterResponse | null> {
  try {
    return await request<GetInviterResponse>(`/inviters/${encodeURIComponent(inviterId)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Submit a registration form.
 * POST /registrations (public)
 */
export async function submitRegistration(
  payload: SubmitRegistrationRequest,
): Promise<SubmitRegistrationResponse> {
  return request<SubmitRegistrationResponse>("/registrations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * List all registration records.
 * GET /admin/registrations (requires auth)
 */
export async function listRegistrations(): Promise<ListRegistrationsResponse> {
  return request<ListRegistrationsResponse>("/admin/registrations");
}

/**
 * List all inviters with their generated URLs.
 * GET /admin/inviters (requires auth)
 */
export async function listInviters(): Promise<ListInvitersResponse> {
  return request<ListInvitersResponse>("/admin/inviters");
}

/**
 * Set or clear the registration limit for an inviter.
 * PATCH /admin/inviters/{inviterId}/limit (requires auth)
 */
export async function setInviterLimit(
  inviterId: string,
  maxCount: number | null,
): Promise<void> {
  await request<{ status: "ok" }>(
    `/admin/inviters/${encodeURIComponent(inviterId)}/limit`,
    {
      method: "PATCH",
      body: JSON.stringify({ maxCount }),
    },
  );
}

/**
 * Set or clear the success QR code image URL for an inviter.
 * PATCH /admin/inviters/{inviterId}/success-qr (requires auth)
 * @param successQrUrl URL of the image, or null to remove it
 */
export async function setSuccessQr(
  inviterId: string,
  successQrUrl: string | null,
): Promise<void> {
  await request<{ status: "ok" }>(
    `/admin/inviters/${encodeURIComponent(inviterId)}/success-qr`,
    {
      method: "PATCH",
      body: JSON.stringify({ successQrUrl }),
    },
  );
}

/**
 * Update an inviter's displayName and/or note.
 * PATCH /admin/inviters/{inviterId} (requires auth)
 */
export async function updateInviter(
  inviterId: string,
  updates: { displayName?: string; note?: string | null },
): Promise<void> {
  await request<{ status: "ok" }>(
    `/admin/inviters/${encodeURIComponent(inviterId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
  );
}

/**
 * Get a presigned URL for uploading a success QR image to S3.
 * PATCH /admin/inviters/{inviterId}/success-qr (requires auth)
 * @returns { uploadUrl, imageUrl }
 */
export async function getSuccessQrUploadUrl(
  inviterId: string,
  contentType: string,
): Promise<{ uploadUrl: string; imageUrl: string }> {
  return request<{ uploadUrl: string; imageUrl: string }>(
    `/admin/inviters/${encodeURIComponent(inviterId)}/success-qr`,
    {
      method: "PATCH",
      body: JSON.stringify({ action: "getUploadUrl", contentType }),
    },
  );
}

/**
 * Regenerate a poster for the given inviter (re-enqueues the render job).
 * POST /admin/inviters/{inviterId}/poster/regenerate (requires auth)
 */
export async function regeneratePoster(inviterId: string): Promise<void> {
  await request<{ status: "ok" }>(
    `/admin/inviters/${encodeURIComponent(inviterId)}/poster/regenerate`,
    { method: "POST" },
  );
}
